import React, {
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Chip } from '@mui/material';

import useQuickSearch from '../../../hooks/useQuickSearch';
import { FormType } from '../../Model/Form';

import LabelCheckbox from '../LabelCheckbox';
import FormsTableToolbar from './FormsTableToolbar';
import {
  Container,
  DataGridContainer,
  StyledDataGrid,
} from './styles';

import texts from './texts';

const FormsTable = ({
  rows,
  ActionsCellComponent,
  extraActionsCellProps,
  showArchivedForms,
  handleArchivedToggle,
  showLeadForms,
  handleLeadFormToggle,
  showPostPaymentForms,
  handlePostPaymentFormToggle,
  defaultFormIdByCoachId,
}) => {
  const columns = useMemo(() => [
    {
      field: 'coach',
      headerName: texts.headers.coach,
      quickSearch: true,
      flex: 6,
    },
    {
      field: 'formName',
      headerName: texts.headers.formName,
      quickSearch: true,
      flex: 10,
    },
    {
      field: 'type',
      headerName: texts.headers.formType,
      flex: 4,
    },
    {
      field: 'default',
      headerName: texts.headers.default,
      flex: 3,
      // `isDefault` marks the global master template; a coach's default PPF is the
      // pointer on their coach doc, hence the lookup by coachId.
      valueGetter: ({ row }) => {
        if (row.isDefault) {
          return texts.templateChip;
        }
        return row.type === FormType.POST_PAYMENT && defaultFormIdByCoachId[row.coachId] === row.id
          ? texts.defaultChip
          : '';
      },
      renderCell: ({ row }) => {
        if (row.isDefault) {
          return <Chip size="small" variant="outlined" label={texts.templateChip} />;
        }
        if (row.type === FormType.POST_PAYMENT && defaultFormIdByCoachId[row.coachId] === row.id) {
          return <Chip size="small" color="primary" label={texts.defaultChip} />;
        }
        return null;
      },
    },
    {
      field: 'id',
      headerName: texts.headers.formId,
      flex: 6,
    },
    {
      field: 'action',
      headerName: texts.headers.action,
      disableColumnMenu: true,
      hideSortIcons: true,
      disableReorder: true,
      flex: 15,
      minWidth: 300,
      cellClassName: 'action-column',
      renderCell: ({ row }) => (<ActionsCellComponent row={row} {...extraActionsCellProps} />),
    },
  ], [extraActionsCellProps, defaultFormIdByCoachId]);

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(rows, columns);

  return (
    <Container>
      <DataGridContainer>
        <StyledDataGrid
          rows={quickSearchRows}
          columns={columns}
          rowHeight={60}
          pagination
          pageSize={20}
          rowsPerPageOptions={[20]}
          disableSelectionOnClick
          components={{
            Toolbar: FormsTableToolbar,
          }}
          componentsProps={{
            toolbar: {
              ...toolbarProps,
              placeholder: texts.toolbarPlaceholder,
              filterTools: [
                {
                  Component: LabelCheckbox,
                  id: 'archive-filter',
                  props: {
                    description: texts.archivedCheckbox,
                    onChange: handleArchivedToggle,
                    isChecked: showArchivedForms,
                  },
                },
                {
                  Component: LabelCheckbox,
                  id: 'lead-form-filter',
                  props: {
                    description: texts.leadFormCheckbox,
                    onChange: handleLeadFormToggle,
                    isChecked: showLeadForms,
                  },
                },
                {
                  Component: LabelCheckbox,
                  id: 'post-payment-form-filter',
                  props: {
                    description: texts.postPaymentFormCheckbox,
                    onChange: handlePostPaymentFormToggle,
                    isChecked: showPostPaymentForms,
                  },
                },
              ],
            },
          }}
        />
      </DataGridContainer>
    </Container>
  );
};

FormsTable.propTypes = {
  rows: PropTypes.array.isRequired,
  ActionsCellComponent: PropTypes.elementType.isRequired,
  extraActionsCellProps: PropTypes.object,
  showArchivedForms: PropTypes.bool.isRequired,
  handleArchivedToggle: PropTypes.func.isRequired,
  showLeadForms: PropTypes.bool.isRequired,
  handleLeadFormToggle: PropTypes.func.isRequired,
  showPostPaymentForms: PropTypes.bool.isRequired,
  handlePostPaymentFormToggle: PropTypes.func.isRequired,
  defaultFormIdByCoachId: PropTypes.object,
};

FormsTable.defaultProps = {
  extraActionsCellProps: {},
  defaultFormIdByCoachId: {},
};

export default compose(
  observer,
)(FormsTable);
